import type { RecipientGroup } from '@/types';

export const ANNOUNCEMENT_BATCH_SIZE = 5;
export const ANNOUNCEMENT_BATCH_DELAY_MS = 1100;

export type SendAnnouncementEmailPayload = {
  unitId: string;
  eventId: string;
  announcementId: string;
  message: string;
  subject?: string;
  sendTo: RecipientGroup[];
  includeRsvpLink?: boolean;
  includeRsvpDashboardLink?: boolean;
  includePaymentLink?: boolean;
};

export type SendAnnouncementEmailResult = {
  success: boolean;
  recipientCount: number;
  totalRecipients: number;
  sentSoFar: number;
  message: string;
};

export type SendAnnouncementProgress = {
  sent: number;
  total: number;
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Normalise batch progress from cloud function (supports legacy single-shot responses). */
export function normalizeAnnouncementBatchProgress(data: SendAnnouncementEmailResult): {
  sent: number;
  total: number;
  isLegacyResponse: boolean;
} {
  const isLegacyResponse =
    typeof data.totalRecipients !== 'number' || typeof data.sentSoFar !== 'number';

  if (isLegacyResponse) {
    const total = data.recipientCount ?? 0;
    return { sent: total, total, isLegacyResponse: true };
  }

  return {
    sent: data.sentSoFar,
    total: data.totalRecipients,
    isLegacyResponse: false,
  };
}

/**
 * Sends announcement emails in batches of 5, waiting 1 second between batches.
 */
export async function sendAnnouncementInBatches(
  sendFn: (
    payload: SendAnnouncementEmailPayload & { batchOffset: number; batchSize: number }
  ) => Promise<{ data: SendAnnouncementEmailResult }>,
  payload: SendAnnouncementEmailPayload,
  onProgress?: (progress: SendAnnouncementProgress) => void
): Promise<SendAnnouncementEmailResult> {
  let offset = 0;
  let lastResult: SendAnnouncementEmailResult | null = null;

  while (true) {
    const { data } = await sendFn({
      ...payload,
      batchOffset: offset,
      batchSize: ANNOUNCEMENT_BATCH_SIZE,
    });
    lastResult = data;
    const progress = normalizeAnnouncementBatchProgress(data);
    onProgress?.({ sent: progress.sent, total: progress.total });

    if (progress.isLegacyResponse || progress.sent >= progress.total) {
      break;
    }

    offset += ANNOUNCEMENT_BATCH_SIZE;
    await sleep(ANNOUNCEMENT_BATCH_DELAY_MS);
  }

  if (!lastResult) {
    throw new Error('Announcement send did not return a result');
  }

  const finalProgress = normalizeAnnouncementBatchProgress(lastResult);
  return {
    ...lastResult,
    totalRecipients: finalProgress.total,
    sentSoFar: finalProgress.sent,
  };
}
